import React, { useState } from "react";
import { Eye, Pencil } from "lucide-react";
import NepaliDate from "nepali-date-converter";
import { dynamicFailed } from "../hooks/useToast";
import DocumentPopUp from "../components/DocumentPopUp";

export const formatDate = (date: Date | null) => {
  if (!date) return "-";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const darkenColor = (hex: string, percent: number = 30) => {
  const num = parseInt(hex.replace("#", ""), 16);

  const r = Math.max(0, (num >> 16) - (255 * percent) / 100);
  const g = Math.max(0, ((num >> 8) & 0x00ff) - (255 * percent) / 100);
  const b = Math.max(0, (num & 0x0000ff) - (255 * percent) / 100);

  return `rgb(${r}, ${g}, ${b})`;
};

const fileIconMap: { [extension: string]: string } = {
  doc: "https://w7.pngwing.com/pngs/89/291/png-transparent-microsoft-word-logo-microsoft-word-office-open-xml-document-computer-icons-computer-file-word-file-icon-blue-angle-text-thumbnail.png",
  docx: "https://w7.pngwing.com/pngs/89/291/png-transparent-microsoft-word-logo-microsoft-word-office-open-xml-document-computer-icons-computer-file-word-file-icon-blue-angle-text-thumbnail.png",
  docs: "https://w7.pngwing.com/pngs/89/291/png-transparent-microsoft-word-logo-microsoft-word-office-open-xml-document-computer-icons-computer-file-word-file-icon-blue-angle-text-thumbnail.png",

  xls: "https://cdn-icons-png.flaticon.com/512/732/732220.png",
  xlsx: "https://cdn-icons-png.flaticon.com/512/732/732220.png",
};

const defaultIcon =
  "https://cdn4.iconfinder.com/data/icons/essentials-72/24/007_-_File-512.png";

export const getFileIcon = (path: string) => {
  const match = path.match(/\.(\w+)$/i);

  if (match) {
    const ext = match[1].toLowerCase();
    return fileIconMap[ext] || defaultIcon;
  }

  return defaultIcon;
};

const getFileType = (fileName?: string) => {
  if (!fileName) return "";

  return fileName.split(".").pop()?.toLowerCase() || "";
};

export const FilePreview: React.FC<{
  file: File | string | null;
  fileName: string | undefined;
  onChange: any;
}> = ({ file, fileName, onChange }) => {
  const [showDoc, setShowDoc] = useState(false);

  if (!file) return null;

  const url = file instanceof File ? URL.createObjectURL(file) : file;
  const effectiveFileName = fileName || (file instanceof File ? file.name : "");
  const ext = getFileType(effectiveFileName || url);
  const isImage = ["jpg", "jpeg", "png", "webp"].includes(ext);
  const isPdf = ext === "pdf";

  return (
    <div>
      {isImage && (
        <div className="image-preview-wrapper position-relative">
          <img src={url} alt="preview" className="file-preview-image" />

          <button
            type="button"
            className="image-view-overlay btn btn-badge text-main-color px-3 mb-0"
            onClick={() => setShowDoc(true)}
            title="View Document"
          >
            <Eye size={16} />
          </button>

          <label className="image-edit-overlay btn btn-badge text-main-color px-3 mb-0">
            <Pencil size={16} />

            <input
              type="file"
              hidden
              accept="image/jpeg,image/png"
              name="file"
              onChange={onChange}
            />
          </label>
        </div>
      )}

      {isPdf && (
        <div className="image-preview-wrapper position-relative">
          <img
            src="https://cdn-icons-png.flaticon.com/512/4208/4208479.png"
            alt="Audit preview"
            className="file-preview-image"
          />

          <button
            type="button"
            className="image-view-overlay btn btn-badge text-main-color px-3 mb-0"
            onClick={() => setShowDoc(true)}
            title="View Document"
          >
            <Eye size={16} />
          </button>

          <label className="image-edit-overlay btn btn-badge text-main-color px-3 mb-0">
            <Pencil size={16} />
            <input type="file" hidden name="file" onChange={onChange} />
          </label>
        </div>
      )}

      {!isImage && !isPdf && (
        <div className="image-preview-wrapper position-relative">
          <img
            src={getFileIcon(effectiveFileName || "")}
            alt="file"
            className="file-preview-image"
          />

          <button
            type="button"
            className="image-view-overlay btn btn-badge text-main-color px-3 mb-0"
            onClick={() => setShowDoc(true)}
            title="View Document"
          >
            <Eye size={16} />
          </button>

          <label className="image-edit-overlay btn btn-badge text-main-color px-3 mb-0">
            <Pencil size={16} />
            <input type="file" hidden name="file" onChange={onChange} />
          </label>
        </div>
      )}

      <DocumentPopUp
        showDoc={showDoc}
        setShowDoc={setShowDoc}
        url={url}
        fileName={effectiveFileName}
        fileType={file instanceof File ? file.type : undefined}
      />
    </div>
  );
};

export const ExistingFilePreview: React.FC<{
  documentUrl: string | null | undefined;
  onChange: any;
}> = ({ documentUrl, onChange }) => {
  const [showDoc, setShowDoc] = useState(false);

  if (!documentUrl) return null;

  const cleanPath = documentUrl.replace(/\\/g, "/");
  const url = `${process.env.REACT_APP_Url}/${cleanPath}`;

  // get extension from documentUrl
  const ext = cleanPath.split("?")[0].split(".").pop()?.toLowerCase();
  const isImage = ["jpg", "jpeg", "png", "webp"].includes(ext || "");
  const isPdf = ext === "pdf";

  return (
    <div>
      {isImage && (
        <div className="image-preview-wrapper position-relative">
          <img src={url} alt="preview" className="file-preview-image" />

          <button
            type="button"
            className="image-view-overlay btn btn-badge text-main-color px-3 mb-0"
            onClick={() => setShowDoc(true)}
            title="View Document"
          >
            <Eye size={16} />
          </button>

          <label className="image-edit-overlay btn btn-badge text-main-color px-3 mb-0">
            <Pencil size={16} />

            <input
              hidden
              accept="image/jpeg,image/png"
              type="file"
              name="file"
              onChange={onChange}
            />
          </label>
        </div>
      )}

      {isPdf && (
        <div className="image-preview-wrapper position-relative">
          <img
            src="https://cdn-icons-png.flaticon.com/512/4208/4208479.png"
            alt="Audit preview"
            className="file-preview-image"
          />

          <button
            type="button"
            className="image-view-overlay btn btn-badge text-main-color px-3 mb-0"
            onClick={() => setShowDoc(true)}
            title="View Document"
          >
            <Eye size={16} />
          </button>

          <label className="image-edit-overlay btn btn-badge text-main-color px-3 mb-0">
            <Pencil size={16} />

            <input hidden type="file" name="file" onChange={onChange} />
          </label>
        </div>
      )}

      {!isImage && !isPdf && (
        <div className="image-preview-wrapper position-relative">
          <img
            src={getFileIcon(cleanPath)}
            alt="file"
            className="file-preview-image"
          />

          <button
            type="button"
            className="image-view-overlay btn btn-badge text-main-color px-3 mb-0"
            onClick={() => setShowDoc(true)}
            title="View Document"
          >
            <Eye size={16} />
          </button>

          <label className="image-edit-overlay btn btn-badge text-main-color px-3 mb-0">
            <Pencil size={16} />

            <input hidden type="file" name="file" onChange={onChange} />
          </label>
        </div>
      )}

      <DocumentPopUp
        showDoc={showDoc}
        setShowDoc={setShowDoc}
        url={url}
        docs={cleanPath}
      />
    </div>
  );
};

export const renderFilePreview = (
  file: File | string | null,
  fileName: string | undefined,
  onChange: any,
) => {
  if (!file) return null;
  return <FilePreview file={file} fileName={fileName} onChange={onChange} />;
};

export const renderExistingFilePreview = (
  documentUrl: string | null | undefined,
  onChange: any,
) => {
  if (!documentUrl) return null;
  return <ExistingFilePreview documentUrl={documentUrl} onChange={onChange} />;
};

export const fileToBase64 = (
  file: File,
): Promise<{
  base64: string;
  name: string;
  type: string;
}> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onload = () =>
      resolve({
        base64: reader.result as string,
        name: file.name,
        type: file.type,
      });

    reader.onerror = (error) => reject(error);
  });
};

export const base64ToFile = async (
  base64: string,
  fileName: string,
  mimeType: string,
): Promise<File> => {
  const res = await fetch(base64);
  const blob = await res.blob();

  return new File([blob], fileName, {
    type: mimeType,
  });
};

export const checkNullDate = (date: string | null | undefined) => {
  return !date || date === "0000-00-00" || date === "1899-11-30" ? "" : date;
};

export const TableFilePreview: React.FC<{
  file: File | string | null;
  fileName: string | undefined;
}> = ({ file, fileName }) => {
  if (!file) return null;

  const url = file instanceof File ? URL.createObjectURL(file) : file;
  const effectiveFileName = fileName || (file instanceof File ? file.name : "");
  const ext = getFileType(effectiveFileName || url);
  const isImage = ["jpg", "jpeg", "png", "webp"].includes(ext);
  const isPdf = ext === "pdf";

  return (
    <div className="d-flex align-items-center">
      <div>
        {isImage && (
          <div className="image-preview-wrapper">
            <img
              src={url}
              alt="Audit preview"
              className="file-preview-image"
              style={{ height: "45px", width: "35px" }}
            />
          </div>
        )}

        {isPdf && (
          <div className="position-relative">
            <img
              src="https://cdn-icons-png.flaticon.com/512/4208/4208479.png"
              alt="Audit preview"
              className="file-preview-image"
              style={{ height: "40px", width: "40px" }}
            />
          </div>
        )}

        {!isImage && !isPdf && (
          <div className="image-preview-wrapper">
            <img
              src={getFileIcon(effectiveFileName || "")}
              alt="Audit preview"
              className="file-preview-image"
              style={{ height: "40px", width: "40px" }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export const TableExistingFilePreview: React.FC<{
  documentUrl: string | null | undefined;
}> = ({ documentUrl }) => {
  // const [showDoc, setShowDoc] = useState(false);

  if (!documentUrl) return null;

  const cleanPath = documentUrl.replace(/\\/g, "/");
  const url = `${process.env.REACT_APP_Url}/${cleanPath}`;
  const ext = cleanPath.split("?")[0].split(".").pop()?.toLowerCase();
  const isImage = ["jpg", "jpeg", "png", "webp"].includes(ext || "");
  const isPdf = ext === "pdf";

  return (
    <div className="d-flex align-items-center">
      <div
      // className="cursor-pointer"
      // onClick={() => setShowDoc(true)}
      // title="Click to view"
      >
        {isImage && (
          <div className="image-preview-wrapper">
            <img
              src={url}
              alt="preview"
              className="file-preview-image"
              style={{ height: "35px", width: "45px" }}
            />
          </div>
        )}

        {isPdf && (
          <div className="position-relative">
            <img
              src="https://cdn-icons-png.flaticon.com/512/4208/4208479.png"
              alt="Audit preview"
              className="file-preview-image"
              style={{ height: "40px", width: "40px" }}
            />
          </div>
        )}

        {!isImage && !isPdf && (
          <div className="image-preview-wrapper">
            <img
              src={getFileIcon(cleanPath)}
              alt="file"
              className="file-preview-image"
              style={{ height: "40px", width: "40px" }}
            />
          </div>
        )}
      </div>

      {/* <DocumentPopUp
        showDoc={showDoc}
        setShowDoc={setShowDoc}
        url={url}
        docs={cleanPath}
      /> */}
    </div>
  );
};

export const renderTableFilePreview = (
  file: File | string | null,
  fileName: string | undefined,
) => {
  if (!file) return null;
  return <TableFilePreview file={file} fileName={fileName} />;
};

export const renderTableExistingFilePreview = (
  documentUrl: string | null | undefined,
) => {
  if (!documentUrl) return null;
  return <TableExistingFilePreview documentUrl={documentUrl} />;
};

export const formatNepaliDate = (date: string) => {
  if (!date) return "";
  const [year, month, day] = date.split(/[-/]/);

  if (!year || !month || !day) return date.replace(/-/g, "/");

  // Remove leading zero if present
  const m = month.replace(/^०/, "").replace(/^0/, "");
  const d = day.replace(/^०/, "").replace(/^0/, "");

  return `${m} / ${d} / ${year}`;
};

export const toISODate = (input: string | null | undefined): string => {
  if (!input?.trim()) return "";

  const parts = input.trim().split(/[-/]/);

  if (parts.length !== 3) return "";

  let year: number;
  let month: number;
  let day: number;

  if (parts[0].length === 4) {
    year = Number(parts[0]);
    month = Number(parts[1]);
    day = Number(parts[2]);
  } else if (parts[2].length === 4) {
    month = Number(parts[0]);
    day = Number(parts[1]);
    year = Number(parts[2]);
  } else {
    return "";
  }

  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    year < 1 ||
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return "";
  }

  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

export const convertNepaliToEnglishDate = (
  nepaliDate: string,
  englishDate?: string,
): string => {
  // Normalize an existing English date
  if (englishDate) {
    return toISODate(englishDate) ?? "";
  }

  if (!nepaliDate) return "";

  try {
    const parts = nepaliDate.split(/[-/]/);

    let year: number;
    let month: number;
    let day: number;

    if (parts.length === 3) {
      if (parts[0].length === 4) {
        // YYYY/MM/DD or YYYY-MM-DD
        year = parseInt(parts[0], 10);
        month = parseInt(parts[1], 10);
        day = parseInt(parts[2], 10);
      } else {
        // MM/DD/YYYY
        month = parseInt(parts[0], 10);
        day = parseInt(parts[1], 10);
        year = parseInt(parts[2], 10);
      }

      if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
        const npDate = new NepaliDate(year, month - 1, day);
        const jsDate = npDate.toJsDate();

        const y = jsDate.getFullYear();
        const m = String(jsDate.getMonth() + 1).padStart(2, "0");
        const d = String(jsDate.getDate()).padStart(2, "0");

        return `${y}-${m}-${d}`;
      }
    }
  } catch (err) {
    console.error("Failed to convert Nepali date to English:", err);
  }

  return toISODate(nepaliDate) ?? "";
};

export const formatDateBySetting = (
  dateStr: string | null | undefined,
  pattern?: string | null,
): string => {
  if (!dateStr) return "";

  let year = "";
  let month = "";
  let day = "";

  const cleanDateStr = dateStr.trim();

  const isoMatch = cleanDateStr.match(
    /^(\d{4})\s*[-/.]\s*(\d{1,2})\s*[-/.]\s*(\d{1,2})/,
  );
  if (isoMatch) {
    year = isoMatch[1];
    month = isoMatch[2].padStart(2, "0");
    day = isoMatch[3].padStart(2, "0");
  } else {
    const parts = cleanDateStr.split(/\s*[-/.]\s*/);
    if (parts.length === 3) {
      if (parts[2].trim().length === 4) {
        month = parts[0].trim().padStart(2, "0");
        day = parts[1].trim().padStart(2, "0");
        year = parts[2].trim();
      } else if (parts[0].trim().length === 4) {
        year = parts[0].trim();
        month = parts[1].trim().padStart(2, "0");
        day = parts[2].trim().padStart(2, "0");
      }
    }
  }

  if (!year || !month || !day) return dateStr.replace(/-/g, "/");

  if (!pattern) return `${year} / ${month} / ${day}`;

  const normalizedPattern = pattern.trim();

  if (
    normalizedPattern === "MM / DD / YYYY" ||
    normalizedPattern === "MM/DD/YYYY"
  ) {
    return `${month} / ${day} / ${year}`;
  }
  if (
    normalizedPattern === "DD / MM / YYYY" ||
    normalizedPattern === "DD/MM/YYYY"
  ) {
    return `${day} / ${month} / ${year}`;
  }
  if (
    normalizedPattern === "YYYY / MM / DD" ||
    normalizedPattern === "YYYY/MM/DD"
  ) {
    return `${year} / ${month} / ${day}`;
  }

  return normalizedPattern
    .replace(/YYYY/g, year)
    .replace(/MM/g, month)
    .replace(/DD/g, day);
};

export const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB in bytes

export const validateFileSize = (
  file: File | null | undefined,
  maxSizeInBytes: number = MAX_FILE_SIZE,
  customErrorMessage?: string,
): boolean => {
  if (!file) return true;

  if (file.size > maxSizeInBytes) {
    dynamicFailed(customErrorMessage || `File size exceeds the 15 mb limit.`);
    return false;
  }
  return true;
};

export const validatePassportSizePhoto = (
  file: File | null | undefined,
): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!file) {
      resolve(true);
      return;
    }

    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      const ratio = image.width / image.height;
      const expectedRatio = 35 / 45;

      URL.revokeObjectURL(objectUrl);

      if (Math.abs(ratio - expectedRatio) > 0.03) {
        dynamicFailed("Passport-size photo required (35 × 45 mm).");
        resolve(false);
        return;
      }

      resolve(true);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      dynamicFailed("Unable to read the uploaded image.");
      resolve(false);
    };

    image.src = objectUrl;
  });
};

export const getCooperativeDisplayName = (
  value?: Record<string, string> | string | any,
): string => {
  if (!value) return "";
  if (typeof value === "object" && value !== null) {
    if (Array.isArray(value)) {
      return value
        .map((item: any) =>
          typeof item === "object" && item !== null ? item.name : String(item),
        )
        .join(", ");
    }
    return Object.values(value).join(", ");
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === "object" && parsed !== null) {
        if (Array.isArray(parsed)) {
          return parsed
            .map((item: any) =>
              typeof item === "object" && item !== null
                ? item.name
                : String(item),
            )
            .join(", ");
        }
        return Object.values(parsed).join(", ");
      }
    } catch {
      return value;
    }
  }
  return String(value);
};
